import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, Refresh } from '@mui/icons-material';
import i18next from 'i18next';

interface PasswordResetDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: (password: string) => void;
  title: string;
  loading?: boolean;
}

const generateRandomPassword = (length = 12): string => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

/**
 * Dialog for resetting user passwords.
 * Handles password reset form submission and validation.
 */
export default function PasswordResetDialog(props: PasswordResetDialogProps) {
  const { open, setOpen, onConfirm, title, loading = false } = props;
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword(generateRandomPassword());
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(password);
  };

  const handleClose = () => {
    setOpen(false);
    setPassword('');
    setShowPassword(false);
  };

  const handleGenerateNew = () => {
    setPassword(generateRandomPassword());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 1 }}>
          <Grid item xs={12}>
            {i18next.t('user-reset-password-description')}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={i18next.t('user-new-password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleGenerateNew}
                      edge="end"
                      title={i18next.t('user-generate-new-password') || ''}
                    >
                      <Refresh />
                    </IconButton>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose} color="secondary" disabled={loading}>
          {i18next.t('confirmation-dialog-decline')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          color="primary"
          disabled={!password.trim() || loading}
        >
          {i18next.t('user-reset-password-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}